/**
 * Version-skew detection — pure logic.
 *
 * Theme 4: closes the stale-tab bug class. A tab left open across a deploy keeps
 * running the OLD JS bundle and re-exposes already-fixed bugs. This module
 * provides the comparison + fetch primitives; the wiring (triggers, prompt) lives
 * in `useVersionCheck` + `VersionUpdateBanner`.
 *
 * Design (locked):
 *   - The running app knows its OWN version via `VITE_APP_VERSION` (baked at build
 *     by the deploy workflow: local-dev / staging-<sha> / v-tag / preview-prN).
 *   - The server publishes the DEPLOYED version in `/version.json`, served
 *     no-cache by Firebase Hosting. (See firebase.json headers + the vite plugin
 *     that emits dist/version.json with the same build-time value.)
 *   - When server.version !== app.version → a NEW deploy happened while this tab
 *     was open → prompt to refresh.
 *
 * NOTE: prompt-first. Nothing in this module reloads the page. The user clicks
 * Refresh. The dirty-state guard lives in `useVersionCheck`.
 */

/** The version baked into THIS running bundle at build time. */
export const APP_VERSION: string = String(
  import.meta.env.VITE_APP_VERSION ?? 'dev'
);

/** Shape of the published /version.json artifact. */
export interface VersionManifest {
  /** The deployed build version. Matches VITE_APP_VERSION at build time. */
  version: string;
  /**
   * Optional. The oldest client version still considered usable. If the running
   * app's version is BELOW this, the prompt may be shown more aggressively (a
   * future critical-fix lever). Absent in normal deploys.
   */
  minSupportedVersion?: string;
}

/** The well-known path Firebase Hosting serves no-cache. */
export const VERSION_MANIFEST_PATH = '/version.json';

/**
 * Versions that are NEVER treated as a real skew, regardless of the server value.
 * `dev` / `local-dev` are local builds where version.json (if present) is stale
 * or irrelevant; an empty string means the env var was missing. Prompting in
 * these cases would be noise, not signal.
 */
const NON_COMPARABLE = new Set(['', 'dev', 'local-dev']);

/**
 * Decide whether a refresh prompt should be shown.
 *
 * Returns true ONLY when both versions are real, comparable strings and they
 * differ. Identical versions, or either side non-comparable (dev/local/empty),
 * return false — no prompt.
 */
export function isVersionMismatch(
  current: string | null | undefined,
  latest: string | null | undefined
): boolean {
  const cur = (current ?? '').trim();
  const next = (latest ?? '').trim();

  if (NON_COMPARABLE.has(cur) || NON_COMPARABLE.has(next)) return false;
  if (!cur || !next) return false;

  return cur !== next;
}

/**
 * Fetch the published version manifest, bypassing all caches.
 *
 * The app runs behind a service worker (vite-plugin-pwa, NetworkFirst for the
 * app shell). To guarantee we read the freshly-deployed value and never a cached
 * copy, we use `cache: 'no-store'` AND a cache-busting query param — the query
 * param defeats both the HTTP cache and any service-worker route match on the
 * bare `/version.json` URL.
 *
 * Returns null on any failure (offline, 404, parse error). A null result must
 * NEVER trigger a prompt — absence of signal is not a mismatch.
 */
export async function fetchLatestVersion(
  fetchImpl: typeof fetch = fetch
): Promise<VersionManifest | null> {
  try {
    const url = `${VERSION_MANIFEST_PATH}?t=${Date.now()}`;
    const res = await fetchImpl(url, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as unknown;
    if (
      data &&
      typeof data === 'object' &&
      typeof (data as { version?: unknown }).version === 'string'
    ) {
      return data as VersionManifest;
    }
    return null;
  } catch {
    return null;
  }
}
